import { Reveal, Eyebrow } from "./Reveal";
import { Building2, Home, Droplets, Zap, Wind, Sun, KeyRound, Check, ArrowRight } from "lucide-react";
import { useLang } from "../i18n";

const IMGS = {
  ristrutturazioni:
    "https://images.pexels.com/photos/30211382/pexels-photo-30211382.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  costruzioni:
    "https://images.unsplash.com/photo-1599777560450-e462cffc5368?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjB2aWxsYSUyMGR1c2slMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzkyNDM5NDI3fDA&ixlib=rb-4.1.0&q=85",
  solare:
    "https://images.unsplash.com/photo-1770391949357-3c27554d3a78?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBpbnRlcmlvciUyMHJlbm92YXRpb24lMjBsdXh1cnklMjBhcGFydG1lbnQlMjBzb2xhciUyMHBhbmVsJTIwYmx1ZXByaW50fGVufDB8fHx8MTc5MjQzOTQzNHww&ixlib=rb-4.1.0&q=85",
  chiavi:
    "https://images.pexels.com/photos/7641932/pexels-photo-7641932.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const Tag = ({ children }) => (
  <span className="inline-flex items-center gap-1.5 text-xs text-slate-300 border border-white/15 rounded-full px-3 py-1.5 bg-black/30 backdrop-blur-sm">
    <Check size={12} className="text-[#D4AF37]" />
    {children}
  </span>
);

const MoreLink = ({ href, testid }) => {
  const { t } = useLang();
  return (
    <a
      href={href}
      data-testid={testid}
      className="group mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-[#D4AF37] hover:text-[#F3E08B] transition-colors"
    >
      {t("Scopri di più", "Learn more")}
      <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
    </a>
  );
};

const ImageCard = ({ testid, img, icon: Icon, title, subtitle, tags, link, linkTestid, className = "", imgClass = "h-44" }) => (
  <article data-testid={testid} className={`trace-card group relative overflow-hidden rounded-2xl bg-[#0E1726] ${className}`}>
    <div className={`relative overflow-hidden ${imgClass}`}>
      <img
        src={img}
        alt={title}
        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0E1726] via-[#0E1726]/20 to-transparent" />
    </div>
    <div className="relative p-6 sm:p-7">
      <div className="flex items-center gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#D4AF37]/12 border border-[#D4AF37]/40">
          <Icon size={18} className="text-[#D4AF37]" />
        </span>
        <h3 className="font-serif text-xl sm:text-2xl font-medium text-white">{title}</h3>
      </div>
      <p className="mt-3 text-sm text-slate-400 leading-relaxed">{subtitle}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {tags.map((tag) => (
          <Tag key={tag}>{tag}</Tag>
        ))}
      </div>
      {link && <MoreLink href={link} testid={linkTestid} />}
    </div>
  </article>
);

const SmallCard = ({ testid, icon: Icon, title, subtitle, link, linkTestid }) => (
  <article
    data-testid={testid}
    className="trace-card group rounded-2xl bg-[#0E1726] p-6 flex flex-col justify-between min-h-[180px] hover:-translate-y-1"
  >
    <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#D4AF37]/12 border border-[#D4AF37]/40 transition-transform duration-300 group-hover:scale-110">
      <Icon size={20} className="text-[#D4AF37]" />
    </span>
    <div>
      <h3 className="font-serif text-lg sm:text-xl font-medium text-white mt-5">{title}</h3>
      <p className="mt-2 text-sm text-slate-400 leading-relaxed">{subtitle}</p>
      {link && <MoreLink href={link} testid={linkTestid} />}
    </div>
  </article>
);

export const Servizi = () => {
  const { t } = useLang();

  return (
    <section id="servizi" data-testid="servizi-section" className="relative py-24 sm:py-32 bg-[#0A1220]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="max-w-2xl">
          <Eyebrow>{t("I nostri servizi", "Our services")}</Eyebrow>
          <h2 className="mt-5 font-display font-semibold text-white text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">
            {t("Un unico interlocutore", "One single partner")}{" "}
            <span className="font-serif italic font-medium text-gold-gradient">
              {t("per ogni esigenza", "for every need")}
            </span>
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          <Reveal className="md:col-span-2 lg:row-span-2" delay={0.05}>
            <ImageCard
              testid="service-card-ristrutturazioni"
              img={IMGS.ristrutturazioni}
              icon={Home}
              title={t("Ristrutturazioni complete", "Complete renovations")}
              subtitle={t("Valorizziamo gli spazi, miglioriamo la qualità della vita.", "We enhance spaces, we improve quality of life.")}
              tags={[t("Appartamenti", "Apartments"), t("Condomini", "Condominiums"), t("Locali commerciali", "Commercial spaces"), t("Edifici storici", "Historic buildings")]}
              link="/servizi/ristrutturazioni"
              linkTestid="service-card-ristrutturazioni-link"
              className="h-full"
              imgClass="h-52 lg:h-[calc(100%-265px)] lg:min-h-[240px]"
            />
          </Reveal>

          <Reveal className="md:col-span-2 lg:col-span-2" delay={0.12}>
            <ImageCard
              testid="service-card-nuove-costruzioni"
              img={IMGS.costruzioni}
              icon={Building2}
              title={t("Nuove costruzioni", "New builds")}
              subtitle={t("Soluzioni moderne, efficienti e sostenibili.", "Modern, efficient and sustainable solutions.")}
              tags={[t("Residenziali", "Residential"), t("Commerciali", "Commercial"), t("Direzionali", "Offices")]}
              link="/servizi/nuove-costruzioni"
              linkTestid="service-card-nuove-costruzioni-link"
              className="h-full"
            />
          </Reveal>

          <Reveal delay={0.18}>
            <SmallCard
              testid="service-card-idraulici"
              icon={Droplets}
              title={t("Impianti idraulici", "Plumbing systems")}
              subtitle={t("Efficienza e innovazione per il tuo comfort.", "Efficiency and innovation for your comfort.")}
              link="/servizi/impianti"
              linkTestid="service-card-idraulici-link"
            />
          </Reveal>
          <Reveal delay={0.24}>
            <SmallCard
              testid="service-card-elettrici"
              icon={Zap}
              title={t("Impianti elettrici", "Electrical systems")}
              subtitle={t("Sicurezza e prestazioni a norma, senza compromessi.", "Safe, code-compliant performance, no compromises.")}
              link="/servizi/impianti"
              linkTestid="service-card-elettrici-link"
            />
          </Reveal>

          <Reveal className="md:col-span-2 lg:col-span-2" delay={0.1}>
            <ImageCard
              testid="service-card-chiavi-in-mano"
              img={IMGS.chiavi}
              icon={KeyRound}
              title={t("Lavori chiavi in mano", "Turnkey projects")}
              subtitle={t("Dalla progettazione alla consegna, senza pensieri.", "From design to delivery, without worries.")}
              tags={[
                t("Consulenza e progettazione", "Consulting & design"),
                t("Direzione lavori", "Works management"),
                t("Gestione cantieri", "Site management"),
                t("Controllo qualità", "Quality control"),
              ]}
              link="/servizi/chiavi-in-mano"
              linkTestid="service-card-chiavi-in-mano-link"
              className="h-full"
            />
          </Reveal>

          <Reveal delay={0.16}>
            <SmallCard
              testid="service-card-climatizzazione"
              icon={Wind}
              title={t("Climatizzazione", "Air conditioning")}
              subtitle={t("Il clima ideale in ogni ambiente, tutto l'anno.", "The ideal climate in every room, all year round.")}
              link="/servizi/impianti"
              linkTestid="service-card-climatizzazione-link"
            />
          </Reveal>
          <Reveal delay={0.22}>
            <article
              data-testid="service-card-fotovoltaico"
              className="trace-card group relative overflow-hidden rounded-2xl min-h-[220px] hover:-translate-y-1 transition-transform"
            >
              <img
                src={IMGS.solare}
                alt={t("Fotovoltaico", "Photovoltaics")}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#070D18] via-[#070D18]/55 to-[#070D18]/10" />
              <div className="relative p-6 flex flex-col justify-end h-full min-h-[220px]">
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#070D18]/70 border border-[#D4AF37]/50 mb-4">
                  <Sun size={20} className="text-[#F3E08B]" />
                </span>
                <h3 className="font-serif text-lg sm:text-xl font-medium text-white">{t("Fotovoltaico", "Photovoltaics")}</h3>
                <p className="mt-1 text-sm text-slate-300">{t("Energia pulita per edifici sostenibili.", "Clean energy for sustainable buildings.")}</p>
                <MoreLink href="/servizi/impianti" testid="service-card-fotovoltaico-link" />
              </div>
            </article>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
